import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { combineLatest, interval, ReplaySubject } from 'rxjs';
import { filter, takeUntil, throttle } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import {
  TOAST,
  CONVO,
  PRESENT,
  SHARE_LINK,
  ANYONE_CAN_PRESENT,
  ANYONE_CAN_CHANGE_MUSIC,
  ANYONE_CAN_SHARE,
  ANYONE_CAN_SHARE_TO_GROUP,
} from 'entity/SocialSettings';
import User from 'entity/User';
import { InteractionMode, SocialState } from 'messages/websocket.messages';

import { ModalVideoEffectsComponent } from '../../modal-video-effects/modal-video-effects.component';
import { ActionMenuService } from '../../services/action-menu.service';
import { BadgingService } from '../../services/badging/badging.service';
import { BrowserService } from '../../services/browser.service';
import { ChatService } from '../../services/chat.service';
import { doesBrowserSupportVideoEffects } from '../../services/device/utils';
import { HostService } from '../../services/host-service';
import { PresentationRecorderService } from '../../services/presentation/presentation-recorder.service';
import { PresentationService } from '../../services/presentation/presentation.service';
import { SceneService } from '../../services/scene.service';
import { ShareScreenService } from '../../services/share-screen/share-screen.service';
import {
  GroupShareScreenParticipantService,
  GroupShareState,
} from '../../services/share-screen-group/group-share-screen-participant.service';
import { GroupShareScreenRecorderService } from '../../services/share-screen-group/group-share-screen-recorder.service';
import { GroupShareScreenService } from '../../services/share-screen-group/group-share-screen.service';
import { SideSectionService } from '../../services/side-section.service';
import { PLAYERS_IN_VIEW_INTERVAL } from '../../services/subscription/subscription.types';
import { ToastService } from '../../services/toast/toast.service';
import { Player } from '../../social-pixi-canvas/player';
import { InteractionModePresets } from '../../social-state-machine/social-state.constants';
import { GroupScreenShare, socialAppActions, socialAppSelectors } from '../../state/app';
import { mediaSelectors } from '../../state/media';
import { RoomSizeType } from '../../types/room';
import { SideContainerType } from '../enums/SideContainerType';
import {
  GUESTS_SUB_MENU,
  PRESENTATION_SETTINGS_SUB_MENU,
  ROOM_SETTINGS_SUB_MENU,
  CHAT_SUB_MENU,
  FILE_LIBRARY_SUB_MENU,
} from '../social-menu.types';

@Component({
  selector: 'app-action-menu',
  templateUrl: './action-menu.component.html',
  styleUrls: ['./action-menu.component.scss'],
  standalone: false,
})
export class ActionMenuComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public user?: User;
  @Input() public social?: Social;
  public canStartSocialEvent = true;
  public isHost = false;
  public isPresenter = false;
  public isGuest = false;
  public isPresentationActive = false;
  public isPresentationFull = false;
  public waitingListCount = 0;
  public isLivestreamActive = false;
  public areVideoEffectsApplied = false;
  public isVideoEnabled = true;
  protected isSomeoneElsePresenting = false;

  public interactionMode!: InteractionMode;
  public isPushToTalkEnabled = false;
  public isQAEnabled = false;

  // Group Share
  public isEventActive = false;
  public isUserSharingToGroup = false;
  public groupParticipantScreenShareActive = false;
  private isPlayerOutsideGroupLimit = false;
  public isGroupShareRecording = false;
  private groupShares?: GroupScreenShare;
  private group: Player[] = [];

  // Action Menu Options
  public showToast = true;
  public showConvo = true;
  public showPresentation = true;
  public showPlaylist = true;
  public showArtifacts = true;
  public showShareLink = true;
  public enableAudioSettings = false;
  public isGroupShareEnabled = false;
  public newPresentationMenuEnabled = false;
  public inRoomSettingsMenuEnabled = false;
  public areVideoEffectsEnabled = false;
  public compassDisabled = false;

  // Social Settings
  public allowToast = true;
  public allowConvo = true;
  public allowPresenter = true;
  public allowShareLink = true;
  public allowAnyonePresent = false;
  public allowAnyoneChangeMusic = false;
  public allowAnyoneShareScreen = false;
  public allowGroupShareScreen = false;

  public canEndToast = false;

  public isScreenShareActive = false;

  protected isWideRecording = false;

  public userIsToasting = false;
  public destroyed$ = new ReplaySubject<boolean>(1);
  public groupShareButtonText: GroupShareState = GroupShareState.NOT_SHARING;
  public groupShareButtonEnhancedText: GroupShareState = GroupShareState.NOT_SHARING_ENHANCED;
  protected showSmartBadging?: boolean = false;
  protected socialRoomSetId = '';
  public smartBadgingHasQuestions = false;
  protected enhancedScreenShare = false;

  protected isConnectedRoom = false;

  public itemEditor = false;

  public constructor(
    private dialog: MatDialog,
    public actionMenuService: ActionMenuService,
    private presentationService: PresentationService,
    public presentationRecorderService: PresentationRecorderService,
    private toastService: ToastService,
    private shareScreenService: ShareScreenService,
    private store: Store<AppState>,
    private sideSectionService: SideSectionService,
    private chatService: ChatService,
    private hostService: HostService,
    private browserService: BrowserService,
    private groupShareService: GroupShareScreenService,
    private sceneService: SceneService,
    private groupShareScreenParticipantService: GroupShareScreenParticipantService,
    public groupShareScreenRecorderService: GroupShareScreenRecorderService,
    private badgingService: BadgingService,
  ) {
    super();
    this.setDisplayName('ActionMenuComponent');
  }

  public ngOnInit(): void {
    this.store
      .select(coreSelectors.isGuestUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isGuest) => (this.isGuest = isGuest));

    combineLatest([
      this.store.select(socialAppSelectors.isHost),
      this.sceneService.isFeatureAllowed(TOAST),
      this.sceneService.isFeatureAllowed(CONVO),
      this.sceneService.isFeatureAllowed(PRESENT),
      this.sceneService.isFeatureAllowed(SHARE_LINK),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_PRESENT),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_CHANGE_MUSIC),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_SHARE),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_SHARE_TO_GROUP),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ([
          isHost,
          allowToast,
          allowConvo,
          allowPresenter,
          allowShareLink,
          allowAnyonePresent,
          allowAnyoneChangeMusic,
          allowAnyoneShareScreen,
          allowGroupShareScreen,
        ]) => {
          this.isHost = isHost ?? false;
          this.allowToast = allowToast;
          this.allowConvo = allowConvo;
          this.allowPresenter = allowPresenter;
          this.allowShareLink = allowShareLink;
          this.allowAnyonePresent = allowAnyonePresent;
          this.allowAnyoneChangeMusic = allowAnyoneChangeMusic;
          this.allowAnyoneShareScreen = allowAnyoneShareScreen;
          this.allowGroupShareScreen = allowGroupShareScreen;
        },
      );

    this.store
      .select(socialAppSelectors.isPresentationActiveInAnotherRoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActiveInAnotherRoom) => {
        this.isSomeoneElsePresenting = isPresentationActiveInAnotherRoom;
      });

    this.store
      .select(socialAppSelectors.getRoomSize)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((roomSize) => {
        this.compassDisabled = roomSize === RoomSizeType.MINI;
      });

    // Group Screen Shares
    this.store
      .select(socialAppSelectors.getGroupScreenShares)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screenShares) => {
        this.groupShares = screenShares;
        const userId = this.user?.userId ?? '';
        const screenShare = this.groupShares[userId];
        if (screenShare) {
          this.isGroupShareRecording = !!screenShare?.recordingId;
        }
      });

    this.store
      .select(socialAppSelectors.getGroupShareIds)
      .pipe(takeUntil(this.destroyed$))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe((ids) => {
        this.groupShareScreenRecorderService.handleGroupRecording();
      });

    this.store
      .select(socialAppSelectors.isWideRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWideRecording) => {
        this.isWideRecording = isWideRecording;
      });

    combineLatest([
      this.store.select(socialAppSelectors.isConnectedRoom),
      this.store.select(socialAppSelectors.getCurrentConnectedRoomSet),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isConnectedRoom, currentConnectedRoomCounts]) => {
        this.isConnectedRoom =
          isConnectedRoom && !!currentConnectedRoomCounts?.socials && currentConnectedRoomCounts?.socials?.length > 1;
      });

    // LD FLAGS
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_toast)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showToast) => (this.showToast = showToast));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_convo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showConvo) => (this.showConvo = showConvo));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_performer)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showPresentation) => (this.showPresentation = showPresentation));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_playlist)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showPlaylist) => (this.showPlaylist = showPlaylist));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_artifacts)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showArtifacts) => {
        this.showArtifacts = showArtifacts;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_share_link)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showShareLink) => (this.showShareLink = showShareLink));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_presentation_settings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newPresentationMenuEnabled) => (this.newPresentationMenuEnabled = newPresentationMenuEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_in_room_settings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((inRoomSettingsMenuEnabled) => (this.inRoomSettingsMenuEnabled = inRoomSettingsMenuEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_group_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isEnabled) => (this.isGroupShareEnabled = isEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.video_background_blur)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((videoBackgroundBlur) => {
        this.areVideoEffectsEnabled =
          videoBackgroundBlur &&
          doesBrowserSupportVideoEffects(this.browserService.browserType) &&
          !this.browserService.isAndroidDevice();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.smart_badging)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showSmartBadging) => {
        this.showSmartBadging = showSmartBadging;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enhanced_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enhanced_screen_share) => (this.enhancedScreenShare = enhanced_screen_share));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.item_editor)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((item_editor) => (this.itemEditor = item_editor));

    this.actionMenuService.init();
    this.socialRoomSetId = this.social?.socialRoomSetId ? this.social?.socialRoomSetId : '';

    this.store
      .select(socialAppSelectors.isScreenShareActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isScreenShareActive) => (this.isScreenShareActive = isScreenShareActive));

    this.store
      .select(socialAppSelectors.getToast)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((toast) => {
        if (!toast) {
          this.userIsToasting = false;
          this.canEndToast = false;
          return;
        }
        this.userIsToasting = toast.presenter.userId === this.user?.userId;
        this.canEndToast = this.userIsToasting || this.isHost;
        return;
      });

    this.store
      .select(socialAppSelectors.isPresentationFull)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationFull) => (this.isPresentationFull = isPresentationFull));

    this.store
      .select(socialAppSelectors.isLivestreamActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isLivestreamActive) => (this.isLivestreamActive = isLivestreamActive));

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isVideoEnabled = enabled));

    this.store
      .select(socialAppSelectors.isEventActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isEventActive = active;
      });

    this.store
      .select(socialAppSelectors.isUserSharingScreenToGroup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isUserSharingToGroup = active;
        this.calculateGroupShareButtonText();
        this.calculateEnhancedGroupShareButtonText();
      });

    this.store
      .select(socialAppSelectors.getSocialSharedState)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((state: SocialState) => {
        this.waitingListCount = state.readOnly ? Object.keys(state.readOnly.waitingUsers).length : 0;
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((isUserPresenting: boolean) => {
        this.isPresenter = isUserPresenting;
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => (this.isPresentationActive = isPresentationActive));

    this.store
      .select(socialAppSelectors.isPushToTalkActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isPushToTalkEnabled = active;
      });

    this.store
      .select(socialAppSelectors.isQueuedQAndAActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isQAEnabled = active;
      });

    this.actionMenuService.canStartSocialEvent$.pipe(takeUntil(this.destroyed$)).subscribe((canStart) => {
      this.canStartSocialEvent = canStart;
    });

    this.hostService.requestOpenGuestList$
      .pipe(filter(Boolean), takeUntil(this.destroyed$))
      .subscribe(() => this.showGuests());

    // Check for group when displaying group share option
    this.sceneService.primaryGroupMembers$
      .pipe(
        throttle(() => interval(PLAYERS_IN_VIEW_INTERVAL), { leading: true }),
        takeUntil(this.destroyed$),
      )
      .subscribe((group) => {
        this.group = group;
        const mainPlayer = this.sceneService.getPlayer();
        this.isPlayerOutsideGroupLimit = !!mainPlayer?.groupWarning;
        this.groupShareScreenRecorderService.handleGroupRecording();
      });

    // Check if Group Share is active for participant
    this.groupShareScreenParticipantService.isGroupShareActive$.pipe(takeUntil(this.destroyed$)).subscribe((active) => {
      this.groupParticipantScreenShareActive = active;
    });

    this.checkBadgeHasQuestions().catch((e) => {
      this.error('Failed to get badge questions', e);
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  /** emit shouldCloseMenu when using mobile device*/
  private responsiveMenuValidation(): void {
    if (window.innerWidth < 700 || window.innerHeight < 650) {
      this.sideSectionService.setOpenState(SideContainerType.NONE);
    }
  }

  /** Handle displaying menu options*/

  private userCanPresent(): boolean {
    return (
      this.allowPresenter && this.showPresentation && !this.isPresenter && (this.isHost || this.allowAnyonePresent)
    );
  }

  public userCanScreenShare(): boolean {
    if (this.isPresentationActive) {
      return this.isPresenter && (this.isHost || this.allowAnyoneShareScreen);
    }
    return this.userCanPresent() && this.canStartSocialEvent && (this.isHost || this.allowAnyoneShareScreen);
  }

  public userCanGroupScreenShare(): boolean {
    return (
      this.isGroupShareEnabled &&
      (this.allowGroupShareScreen || this.isHost) &&
      !this.isUserSharingToGroup &&
      !!this.group.length &&
      !this.groupParticipantScreenShareActive &&
      !this.isPlayerOutsideGroupLimit &&
      !this.groupShareScreenParticipantService.isGroupShareFull &&
      !this.groupShareScreenParticipantService.hasMembersInScreenShare
    );
  }

  public showPresentOption(): boolean {
    return !this.isPresentationActive && this.userCanPresent();
  }

  public showJoinOption(): boolean {
    return this.isPresentationActive && this.userCanPresent();
  }

  /** Handle menu actions*/
  public handleToast(): void {
    this.logUserAction('toast', 'clicked');
    this.responsiveMenuValidation();
    this.actionMenuService.handleToast();
  }

  public async handleEndToast(): Promise<void> {
    this.logUserAction('end toast', 'clicked');
    this.responsiveMenuValidation();
    await this.toastService.requestEndToast(this.userIsToasting);
  }

  public handleConvo(): void {
    this.logUserAction('convo', 'clicked');
    this.responsiveMenuValidation();
    this.actionMenuService.handleConvo();
  }

  public async handleQuickPresent(crossRoom = false): Promise<void> {
    this.logUserAction('quick present', 'clicked');
    if (!this.user) {
      return;
    }

    const userCount = this.sceneService.getUserCount();

    const presentationSettings =
      userCount > 25 ? InteractionModePresets.ALL_HANDS : InteractionModePresets.OPEN_AUDIENCE_PARTICIPATION;

    await this.presentationService.startPresentation({
      selectedPresenters: [this.user],
      settings: presentationSettings,
      isCrossRoom: crossRoom,
    });
  }

  public async handleQuickScreenShare(enhanced: boolean): Promise<void> {
    this.logUserAction('quick screen share', 'clicked');

    if (!this.user) {
      return;
    }

    const shareScreenSuccess = await this.shareScreenService.startSharing(enhanced);

    if (shareScreenSuccess) {
      if (!this.isPresentationActive) {
        await this.presentationService.startPresentation({
          selectedPresenters: [this.user],
        });
      }
    }
  }

  private calculateGroupShareButtonText(): void {
    this.groupShareButtonText = this.groupShareScreenParticipantService.groupShareState;
  }

  private calculateEnhancedGroupShareButtonText(): void {
    this.groupShareButtonEnhancedText = this.groupShareScreenParticipantService.groupShareEnhancedState;
  }

  public async handleQuickGroupScreenShare(enhanced: boolean): Promise<void> {
    this.logUserAction('quick group screen share', 'clicked');

    if (!this.user) {
      return;
    }

    await this.groupShareService.startSharing(enhanced);
  }

  public async handleQuickJoinPresentation(): Promise<void> {
    this.logUserAction('quick join presentation', 'clicked');
    await this.presentationService.joinPresentation();
  }

  public handleJoinPresentation(): void {
    this.logUserAction('join presentation', 'clicked');
    this.responsiveMenuValidation();
    this.actionMenuService.handlePresenter();
    // Track Churn Zero Event presentation
    // this.trackChurnZeroEvent(ChurnZeroPresentationType.PRESENTATION);
  }

  public handleMusicPlaylist(): void {
    this.logUserAction('music playlist', 'clicked');
    this.responsiveMenuValidation();
    this.actionMenuService.handleMusicPlaylist();
  }

  public handleShareLink(): void {
    this.logUserAction('share link', 'clicked');
    const firstName = this.social?.user?.firstName || '';
    const lastName = this.social?.user?.lastName || '';
    const { socialURI } = this.social ?? {};

    if (!socialURI) {
      return;
    }

    this.actionMenuService.handleShareLink(firstName, lastName, socialURI);
  }

  public showGuests = (): void => {
    this.logUserAction('guests', 'clicked');
    this.sideSectionService.openSubMenu(GUESTS_SUB_MENU);
  };

  public showChat(): void {
    this.logUserAction('chat', 'clicked');
    this.chatService.resetWindow();
    this.sideSectionService.openSubMenu(CHAT_SUB_MENU);
  }

  public showRoomSettings(): void {
    this.logUserAction('room settings', 'clicked');

    if (this.isHost && this.inRoomSettingsMenuEnabled) {
      this.sideSectionService.openSubMenu(ROOM_SETTINGS_SUB_MENU);
    }
  }

  public showPresentationSettings(): void {
    this.logUserAction('presentation settings', 'clicked');

    if (this.isHost || this.isPresenter) {
      this.sideSectionService.openSubMenu(PRESENTATION_SETTINGS_SUB_MENU);
    }
  }

  public viewFileLibrary() {
    this.sideSectionService.openSubMenu(FILE_LIBRARY_SUB_MENU);
  }

  public handleVideoEffectsClick(): void {
    if (!this.areVideoEffectsEnabled) {
      return;
    }

    this.logUserAction('video effects', 'clicked');
    this.dialog.open<ModalVideoEffectsComponent>(ModalVideoEffectsComponent);
  }

  public editSmartBadge(): void {
    this.store.dispatch(socialAppActions.UPDATE_BADGING_MODAL({ open: true }));
  }

  private async checkBadgeHasQuestions(): Promise<void> {
    // Check if there are questions
    const badgeQuestions = await this.badgingService.getMyBadgeData(this.socialRoomSetId);
    this.smartBadgingHasQuestions = !!badgeQuestions.length;
  }
}
